import { memo, useEffect, useRef, useState } from 'react';
import {
  type ForeignNationalInfo,
  type ForeignNationalInfoInput,
  useForeignNationalInfoQuery,
} from '@access-cms/api-client';

import { InfoIcon } from '../../../components/Icon';
import TextField from '../../../components/TextField';
import Tooltip from '../../../components/Tooltip';
import './ApprovalDetailsForeignNationalContent.css';

interface ApprovalDetailsForeignNationalContentProps {
  /** Application whose Foreign National Information is being authored/read. */
  applicationId?: string;
  onCompletionChange?: (isComplete: boolean) => void;
  onPendingChangesChange?: (hasPendingChanges: boolean) => void;
  /** Lifts the current answers (backend field names) to the parent for persistence. */
  onValuesChange?: (values: ForeignNationalInfoInput) => void;
  /** FN approver view: render the answers read-only instead of editable textareas. */
  readOnly?: boolean;
  showValidation?: boolean;
}

type ForeignNationalFieldKey = 'cmsOrganization' | 'informationAccess' | 'informationSystems' | 'logicalAccessTypes';

const REQUIRED_FIELD_ERROR = 'This field is required.';

const FOREIGN_NATIONAL_FIELDS: { key: ForeignNationalFieldKey; label: string; tooltip: string }[] = [
  {
    key: 'cmsOrganization',
    label: 'What organization within CMS will the employee be working for?',
    tooltip:
      'The organization is a department within the Centers for Medicare & Medicaid Services (CMS) where a person will work. The organization is usually abbreviated. Many begin with the word “Office,” “Division (DIV),” or “Center,” though not all do.',
  },
  {
    key: 'logicalAccessTypes',
    label: 'What types of logical access (identification, authentication, authorization, etc.) will the employee need?',
    tooltip:
      'Logical access ensures that users have only the access needed to perform their duties. Examples include the contractor needing access to identify and remedy requests; authenticate employee EUA ID numbers; authorize access to shared folders; and perform tier 1/2/3 system administrator functions, troubleshoot systems, and take corrective action.',
  },
  {
    key: 'informationAccess',
    label: 'What information (PII, PHI, etc.) will the employee have access to?',
    tooltip:
      'Personally Identifiable Information (PII) is any data that can be used to distinguish, trace, or identify a specific individual, either on its own or when combined with other information. Protected Health Information (PHI) is any demographic or medical information that can identify an individual patient.',
  },
  {
    key: 'informationSystems',
    label: 'What information systems will the employee have access to?',
    tooltip:
      'Information systems consist of hardware, software, data, procedures, and people organized to collect, process, maintain, use, share, distribute, or delete information. Examples include Confluence, Outlook, and CMSConnect.',
  },
];

const EMPTY_VALUES: Record<ForeignNationalFieldKey, string> = {
  cmsOrganization: '',
  informationAccess: '',
  informationSystems: '',
  logicalAccessTypes: '',
};

/**
 * Map the persisted record (backend field names) onto the form's local keys.
 * The only divergence is `logicalAccess` (backend) ↔ `logicalAccessTypes` (form).
 */
function fromRecord(record: ForeignNationalInfo | undefined): Record<ForeignNationalFieldKey, string> {
  if (!record) return EMPTY_VALUES;

  return {
    cmsOrganization: record.cmsOrganization ?? '',
    informationAccess: record.informationAccess ?? '',
    informationSystems: record.informationSystems ?? '',
    logicalAccessTypes: record.logicalAccess ?? '',
  };
}

/** Map the form's local keys back onto the backend field names for save. */
function toInput(values: Record<ForeignNationalFieldKey, string>): ForeignNationalInfoInput {
  return {
    cmsOrganization: values.cmsOrganization,
    informationAccess: values.informationAccess,
    informationSystems: values.informationSystems,
    logicalAccess: values.logicalAccessTypes,
  };
}

function ApprovalDetailsForeignNationalContentComponent({
  applicationId,
  onCompletionChange,
  onPendingChangesChange,
  onValuesChange,
  readOnly = false,
  showValidation = false,
}: ApprovalDetailsForeignNationalContentProps) {
  const baseTestId = 'approval-details-foreign-national-content';

  const [values, setValues] = useState<Record<ForeignNationalFieldKey, string>>(EMPTY_VALUES);

  // Prefill from any previously saved answers. A 404 (no record authored yet) is the
  // expected empty state and simply leaves the form blank.
  const { data: savedRecord } = useForeignNationalInfoQuery(applicationId ?? '', { enabled: !!applicationId });
  const hydratedRef = useRef(false);

  // The record is permanently locked at FNMS approval — view-only for everyone (incl.
  // the COR) thereafter, e.g. on the second COR pass after a VAS return/resubmit. Any
  // save attempt would 409 server-side, so a locked record renders read-only and its
  // values are never lifted to the parent for persistence.
  const isLocked = Boolean((savedRecord as ForeignNationalInfo | undefined)?.lockedAt);
  const effectiveReadOnly = readOnly || isLocked;

  useEffect(() => {
    if (hydratedRef.current || !savedRecord) return;
    hydratedRef.current = true;
    const next = fromRecord(savedRecord as ForeignNationalInfo);
    setValues(next);
    if (!(savedRecord as ForeignNationalInfo).lockedAt) {
      onValuesChange?.(toInput(next));
    }
  }, [onValuesChange, savedRecord]);

  const incompleteFields = FOREIGN_NATIONAL_FIELDS.filter((field) => values[field.key].trim().length === 0);
  const isComplete = incompleteFields.length === 0;

  let validationMessage = '';

  if (showValidation && !effectiveReadOnly) {
    const errorCount = incompleteFields.length;
    validationMessage =
      errorCount > 0
        ? `${errorCount} required ${errorCount === 1 ? 'field' : 'fields'} ${
            errorCount === 1 ? 'is' : 'are'
          } incomplete: ${incompleteFields.map((field) => field.label).join(', ')}`
        : 'All required fields are complete';
  }

  // Read-only (FN approver or locked record) view is always "complete" and never dirty,
  // so it does not block the reviewed-all-sections gate or flag unsaved changes.
  useEffect(() => {
    onCompletionChange?.(effectiveReadOnly ? true : isComplete);
  }, [effectiveReadOnly, isComplete, onCompletionChange]);

  useEffect(() => {
    if (effectiveReadOnly) {
      onPendingChangesChange?.(false);

      return;
    }
    // Dirty = the reviewer changed an answer from the saved baseline (or typed into a
    // never-authored record). This drives the "unsaved changes" guard AND the Clear All
    // enablement, so both react to real changes, not merely to fields being non-empty.
    const savedValues = savedRecord ? fromRecord(savedRecord as ForeignNationalInfo) : EMPTY_VALUES;
    const isDirty = FOREIGN_NATIONAL_FIELDS.some((field) => values[field.key] !== savedValues[field.key]);
    onPendingChangesChange?.(isDirty);
  }, [effectiveReadOnly, onPendingChangesChange, savedRecord, values]);

  const handleChange = (key: ForeignNationalFieldKey, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      onValuesChange?.(toInput(next));

      return next;
    });
  };

  return (
    <div
      className='approval-details-foreign-national-content'
      data-testid={baseTestId}>
      {/* ACP-2092: mounted up front and only its text changes — a live region created
          together with its content is announced inconsistently or not at all. */}
      <div
        aria-atomic='true'
        aria-live='polite'
        className='ds-u-visibility--screen-reader'
        role='status'>
        {validationMessage}
      </div>
      {!effectiveReadOnly && (
        <p className='approval-details-foreign-national-content__intro'>
          Please fill out the following for this person. Work with the contracting company for help answering these
          questions.
        </p>
      )}

      {FOREIGN_NATIONAL_FIELDS.map((field) =>
        effectiveReadOnly ? (
          <div
            key={field.key}
            className='approval-details-foreign-national-content__field'
            data-testid={`${baseTestId}-${field.key}`}>
            <p className='approval-details-foreign-national-content__readonly-label'>
              {field.label}
              <span className='approval-details-foreign-national-content__readonly-label-suffix'>
                <span
                  aria-label='required'
                  className='approval-details-foreign-national-content__required-asterisk'>
                  *
                </span>
                <Tooltip
                  className='approval-details-foreign-national-content__info-tooltip'
                  id={`foreign-national-${field.key}-tooltip`}
                  placement='top'
                  title={field.tooltip}>
                  <InfoIcon />
                </Tooltip>
              </span>
            </p>
            <p className='approval-details-foreign-national-content__readonly-value'>
              {values[field.key].trim().length > 0 ? values[field.key] : '—'}
            </p>
          </div>
        ) : (
          <div
            key={field.key}
            className='approval-details-foreign-national-content__field'>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-${field.key}`}
              errorMessage={showValidation && values[field.key].trim().length === 0 ? REQUIRED_FIELD_ERROR : undefined}
              label={field.label}
              maxLength={2000}
              name={`foreign-national-${field.key}`}
              rows={4}
              tooltipText={field.tooltip}
              value={values[field.key]}
              onChange={(event) => handleChange(field.key, event.target.value)}
            />
          </div>
        ),
      )}
    </div>
  );
}

const ApprovalDetailsForeignNationalContent = memo(ApprovalDetailsForeignNationalContentComponent);

export default ApprovalDetailsForeignNationalContent;
